import { Link } from "@tanstack/react-router";
import type { DashboardData, RouteTrack } from "@/app/queries";
import { Button } from "@/ui/Button";
import { Segments } from "@/ui/Segments";
import { EmptyState } from "@/ui/states";
import { DeadlinesCard } from "./DeadlinesCard";
import { WaitingWidget } from "./WaitingWidget";
import { continueLabel, mainTracks, nextAction, parallelTrack } from "./derive";

function Eyebrow({ children }: { children: string }) {
  return <div className="mb-[9px] font-mono text-[10.5px] uppercase tracking-[.06em] text-mut">{children}</div>;
}

function TrackRow({ track, dashed = false }: { track: RouteTrack; dashed?: boolean }) {
  const active = track.steps.some((s) => s.state === "active");
  const closed = !active && track.done === 0;
  const border = dashed ? "border-dashed border-bd2" : active ? "border-acc" : "border-bd";
  const bg = dashed ? "bg-card" : active ? "bg-accsoft" : "bg-transparent";
  const fg = closed && !dashed ? "text-mut" : active || dashed ? "text-ink" : "text-ink2";
  return (
    <Link
      to="/track/$slug"
      params={{ slug: track.slug }}
      className={`grid grid-cols-[minmax(0,1fr)_92px_36px] items-center gap-2.5 rounded-md border px-2.5 py-2 no-underline ${border} ${bg}`}
      data-testid={`track-${track.slug}`}
    >
      <span className={`overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] ${fg}`}>
        {track.name}
        {dashed && " · параллельно"}
      </span>
      <Segments total={track.total} done={track.done} active={active} height={5} />
      <span className={`text-right font-mono text-[10.5px] ${active && !dashed ? "text-ink2" : "text-mut"}`}>
        {track.done}/{track.total}
      </span>
    </Link>
  );
}

/** Дашборд S8 (390px): «сейчас» с сегментами, сдачи, дедлайны, треки. */
export function MobileDashboard({ data }: { data: DashboardData }) {
  const { route, current, deadlines, cohort } = data;
  const track = route.active_track;
  const main = mainTracks(route.tracks);
  const para = parallelTrack(route.tracks);
  // Параллельный трек — сразу после активного, как в S8.
  const activeIdx = main.findIndex((t) => t.id === track?.id);
  const rows: { t: RouteTrack; dashed: boolean }[] = main.map((t) => ({ t, dashed: false }));
  if (para) rows.splice(activeIdx >= 0 ? activeIdx + 1 : rows.length, 0, { t: para, dashed: true });
  return (
    <div className="flex flex-col gap-3.5">
      <div>
        <h1 className="m-0 font-mono text-[20px] font-bold leading-[1.2] tracking-[-.01em] text-ink">Мой маршрут</h1>
        <div className="mt-0.5 font-mono text-[10.5px] text-mut" data-testid="steps-total">
          {route.done} / {route.total} шагов · {cohort ? cohort.toLowerCase() : "без когорты"}
        </div>
      </div>

      {current && track ? (
        <section className="rounded-lg border border-bd2 bg-card p-3.5" aria-labelledby="now-title">
          <div className="mb-[7px] font-mono text-[10.5px] text-accdim">
            сейчас · {current.track}, шаг {current.step_index} из {current.track_total}
          </div>
          <h2 id="now-title" className="m-0 mb-3 font-mono text-[18px] font-bold leading-[1.2] tracking-[-.01em] text-ink">
            {current.title}
          </h2>
          <div className="mb-3">
            <Segments total={track.total} done={track.done} active height={5} />
          </div>
          <Link to="/step/$id" params={{ id: current.id }} className="block no-underline">
            <Button variant="primary" className="w-full justify-center py-[11px]" tabIndex={-1}>
              {continueLabel(nextAction(current), true)}
            </Button>
          </Link>
        </section>
      ) : (
        <EmptyState where="студент · мой маршрут" title="Маршрут пройден" body="Все гейты сданы. Новые шаги появятся, когда админ добавит их в курс." />
      )}

      <WaitingWidget current={current} compact />

      <div>
        <Eyebrow>сдачи</Eyebrow>
        <EmptyState where="студент · сдачи" title="Сдач пока нет" body="Первая появится, когда отправишь ноутбук ментору." />
      </div>

      <div>
        <Eyebrow>дедлайны</Eyebrow>
        <DeadlinesCard deadlines={deadlines} cohort={cohort} compact />
      </div>

      <div>
        <Eyebrow>треки</Eyebrow>
        <div className="flex flex-col gap-1.5">
          {rows.map(({ t, dashed }) => (
            <TrackRow key={t.id} track={t} dashed={dashed} />
          ))}
        </div>
      </div>
    </div>
  );
}
